import { useEffect, useState } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Table from '../components/Table';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

const fetchLeaveTypeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/lms/leave-types');
  const types = res.data.leaveTypes || [];
  const filtered = search ? types.filter((t: any) => t.name.toLowerCase().includes(search.toLowerCase())) : types;
  return filtered.map((t: any) => ({ value: t.id, label: `${t.name} (${t.code})` }));
};

export default function YearEndProcess() {
  const [fromYear, setFromYear] = useState(String(new Date().getFullYear()));
  const [toYear, setToYear] = useState(String(new Date().getFullYear() + 1));
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [pending, setPending] = useState<any[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [strategy, setStrategy] = useState('ALL');
  const [minCap, setMinCap] = useState('');
  const [maxCap, setMaxCap] = useState('');
  const [expiryMonths, setExpiryMonths] = useState('');
  const [mode, setMode] = useState<'AL' | 'CLOSE'>('AL');

  const loadPending = async () => {
    const res = await api.get('/lms/year-end/pending', { params: { fromFinancialYear: fromYear } });
    setPending((res.data.pending || []).filter((p: any) => !leaveTypeId || p.leave_type_id === leaveTypeId));
  };

  useEffect(() => {
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromYear, leaveTypeId]);

  const process = async () => {
    if (!leaveTypeId || !selected.length) return alert('Select a leave type and at least one employee');
    try {
      if (mode === 'AL') {
        const res = await api.post('/lms/year-end/process', {
          employeeIds: selected, leaveTypeId, fromFinancialYear: fromYear, toFinancialYear: toYear,
          strategy, minCap: minCap ? Number(minCap) : null, maxCap: maxCap ? Number(maxCap) : null,
          expiryMonths: expiryMonths ? Number(expiryMonths) : null,
        });
        alert(`Processed ${res.data.results.length} employee(s)`);
      } else {
        const res = await api.post('/lms/year-end/close-cycle', {
          employeeIds: selected, leaveTypeId, fromFinancialYear: fromYear, toFinancialYear: toYear,
        });
        alert(`Closed cycle for ${res.data.closed} employee(s)`);
      }
      setSelected([]);
      loadPending();
    } catch (err: any) {
      alert(err.response?.data?.message || (err.response?.data?.blockedEmployeeIds ? 'Employees with pending leave applications must be resolved first' : 'Failed to process'));
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Leave Year-End Process</h1>
      <div className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <Input label="From Financial Year" value={fromYear} onChange={(e) => setFromYear(e.target.value)} />
          <Input label="To Financial Year" value={toYear} onChange={(e) => setToYear(e.target.value)} />
          <LookupSelect label="Leave Type" value={leaveTypeId} onChange={setLeaveTypeId} fetchOptions={fetchLeaveTypeOptions} />
          <Select label="Mode" value={mode} onChange={(e) => setMode(e.target.value as any)}
            options={[{ value: 'AL', label: 'AL Carry-Forward Wizard' }, { value: 'CLOSE', label: 'ML/CC One-Click Close' }]} />
          {mode === 'AL' && (
            <>
              <Select label="Carry-Forward Strategy" value={strategy} onChange={(e) => setStrategy(e.target.value)}
                options={[{ value: 'ALL', label: 'All Balance' }, { value: 'LAST_YEAR_ONLY', label: 'Last Year Only (older forfeited)' }, { value: 'MIN_MAX_CAPPED', label: 'Specify Min-Max Cap' }]} />
              {strategy === 'MIN_MAX_CAPPED' && (
                <>
                  <Input label="Min Cap" type="number" value={minCap} onChange={(e) => setMinCap(e.target.value)} />
                  <Input label="Max Cap" type="number" value={maxCap} onChange={(e) => setMaxCap(e.target.value)} />
                </>
              )}
              <Input label="Expiry Months for C/F Leave (optional)" type="number" value={expiryMonths} onChange={(e) => setExpiryMonths(e.target.value)} />
            </>
          )}
        </div>
        <Button onClick={process}>Process Selected ({selected.length})</Button>
      </div>

      <h2 className="font-semibold text-gray-800 mb-2">Pending Year-End ({pending.length})</h2>
      <Table
        columns={[
          {
            key: 'select', header: '',
            render: (r: any) => (
              <input
                type="checkbox"
                checked={selected.includes(r.employee_id)}
                onChange={(e) => setSelected(e.target.checked ? [...selected, r.employee_id] : selected.filter((id) => id !== r.employee_id))}
              />
            ),
          },
          { key: 'code', header: 'Code' },
          { key: 'first_name', header: 'First Name' },
          { key: 'last_name', header: 'Last Name' },
          { key: 'leave_type_name', header: 'Leave Type' },
        ]}
        rows={pending}
      />
    </div>
  );
}
